/**
 * Telegram bot messages editor (epic-002, stories 5–6).
 *
 * Lets a merchant customise their own bot's canned replies (/start, /help,
 * /support, /legal, …) and maintain the FAQ entries used for free-text
 * keyword matching. Every message is an independently versioned catalog item
 * (per-key optimistic locking), so this page saves per key instead of using
 * the scaffold-level save bar. Content is mastered by web-content-service and
 * reaches the bot via the `bot.content.*.v1` projection — changes propagate
 * within about a minute; the bot always falls back to the platform default
 * text when a key is not customised or is disabled.
 */
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import LiveHelpRoundedIcon from '@mui/icons-material/LiveHelpRounded';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import SettingsSuggestRoundedIcon from '@mui/icons-material/SettingsSuggestRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import TerminalRoundedIcon from '@mui/icons-material/TerminalRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import type { NormalizedApiError } from '@/shared/api';
import { EmptyState, LoadingState } from '@/shared/ui';

import { DEFAULT_BOT_MESSAGE_LOCALE } from '../../api/telegramBotMessagesApi';
import {
  useBotMessages,
  useCreateFaqEntry,
  useDeleteFaqEntry,
  useFaqEntries,
  useResetBotMessage,
  useUpdateFaqEntry,
  useUpsertBotMessage,
} from '../../hooks';
import { useDomainPermissions, useFeedback } from '../../lib';
import type { BotMessage, FaqEntry } from '../../model/settingsTypes';
import { FeedbackSnackbar, SettingsCard, SettingsScaffold } from '../components';

const MAX_BODY_LENGTH = 4096;

/** Sample values used by the in-page preview (never sent anywhere). */
const PREVIEW_SAMPLES: Record<string, string> = {
  shop_name: 'Ромашка',
  customer_name: 'Иван',
  support_contact: '@romashka_support',
  mini_app_url: 'https://t.me/romashka_bot/shop',
};

const PLACEHOLDER_PATTERN = /\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g;

interface MessageKeySpec {
  key: string;
  label: string;
  description: string;
}

interface MessageGroupSpec {
  key: string;
  title: string;
  description: string;
  icon: ReactNode;
  keys: MessageKeySpec[];
}

/** Editor grouping/labels for the fixed platform message catalog. */
const MESSAGE_GROUPS: MessageGroupSpec[] = [
  {
    key: 'onboarding',
    title: 'Onboarding',
    description: 'The first replies a customer sees when starting the bot.',
    icon: <RocketLaunchRoundedIcon />,
    keys: [
      {
        key: 'telegram.welcome',
        label: 'Welcome (/start)',
        description: 'Reply to /start for a registered customer.',
      },
      {
        key: 'telegram.registration.prompt',
        label: 'Registration prompt',
        description: 'Reply to /start for a new, unregistered user.',
      },
    ],
  },
  {
    key: 'commands',
    title: 'Commands',
    description: 'Replies to the standard bot commands.',
    icon: <TerminalRoundedIcon />,
    keys: [
      { key: 'telegram.help', label: 'Help (/help)', description: 'The command overview reply.' },
      {
        key: 'telegram.catalog',
        label: 'Catalog (/catalog)',
        description: 'Invites the customer to open the catalog Mini App.',
      },
      {
        key: 'telegram.catalog.disabled',
        label: 'Catalog unavailable',
        description: 'Sent for /catalog while the catalog feature is disabled.',
      },
      {
        key: 'telegram.orders',
        label: 'Orders (/orders)',
        description: 'Where to find the order history.',
      },
      {
        key: 'telegram.profile',
        label: 'Profile (/profile)',
        description: 'Where to manage the customer profile.',
      },
      {
        key: 'telegram.legal',
        label: 'Legal (/legal)',
        description: 'Where to read the legal information.',
      },
    ],
  },
  {
    key: 'support',
    title: 'Support & AI',
    description: 'The support flow and AI assistant notices.',
    icon: <SupportAgentRoundedIcon />,
    keys: [
      {
        key: 'telegram.support.prompt',
        label: 'Support prompt (/support)',
        description: 'Asks the customer to describe their question.',
      },
      {
        key: 'telegram.support.received',
        label: 'Support received',
        description: 'Acknowledges that the question was passed to support.',
      },
      {
        key: 'telegram.support.disabled',
        label: 'Support unavailable',
        description: 'Sent for /support while the support feature is disabled.',
      },
      {
        key: 'telegram.ai.thinking',
        label: 'AI thinking',
        description: 'Shown while the AI assistant prepares an answer.',
      },
      {
        key: 'telegram.ai.failed',
        label: 'AI failed',
        description: 'Sent when the AI assistant could not process the question.',
      },
      {
        key: 'telegram.ai.escalated',
        label: 'AI escalated',
        description: 'Sent when the question is handed over to a human agent.',
      },
    ],
  },
  {
    key: 'contact',
    title: 'Phone confirmation',
    description: 'The share-phone / contact confirmation flow.',
    icon: <PhoneIphoneRoundedIcon />,
    keys: [
      {
        key: 'telegram.contact.confirmation_prompt',
        label: 'Confirmation prompt',
        description: 'Asks the customer to confirm their phone via Telegram.',
      },
      {
        key: 'telegram.contact.rejected',
        label: 'Contact rejected',
        description: 'Sent when a customer shares somebody else’s contact.',
      },
      {
        key: 'telegram.contact.confirmed',
        label: 'Contact confirmed',
        description: 'Phone verified and saved to the profile.',
      },
      {
        key: 'telegram.contact.confirmed_unregistered',
        label: 'Confirmed, not registered',
        description: 'Phone verified but the profile is not complete yet.',
      },
    ],
  },
  {
    key: 'system',
    title: 'System messages',
    description: 'Fallbacks and service notices.',
    icon: <SettingsSuggestRoundedIcon />,
    keys: [
      {
        key: 'telegram.maintenance',
        label: 'Maintenance',
        description: 'Sent while the bot is paused or under maintenance.',
      },
      {
        key: 'telegram.command.unknown',
        label: 'Unknown command',
        description: 'Reply to a command the bot does not recognise.',
      },
      {
        key: 'telegram.callback.accepted',
        label: 'Action accepted',
        description: 'Acknowledges an inline button press.',
      },
      {
        key: 'telegram.fallback',
        label: 'Fallback',
        description: 'Lead text of the reply to unrecognised free-form messages.',
      },
    ],
  },
];

const KNOWN_KEYS = new Set(MESSAGE_GROUPS.flatMap((group) => group.keys.map((spec) => spec.key)));

function substitutePreview(text: string): string {
  return text.replace(PLACEHOLDER_PATTERN, (raw, name: string) => PREVIEW_SAMPLES[name] ?? raw);
}

function findUnknownPlaceholders(text: string, allowed: string[]): string[] {
  const allowedSet = new Set(allowed);
  const unknown = new Set<string>();
  for (const match of text.matchAll(PLACEHOLDER_PATTERN)) {
    if (!allowedSet.has(match[1])) {
      unknown.add(match[1]);
    }
  }
  return [...unknown];
}

interface MessageRowProps {
  message: BotMessage;
  spec: MessageKeySpec;
  canManage: boolean;
  saving: boolean;
  onSave: (message: BotMessage, body: string, enabled: boolean) => void;
  onResetRequest: (message: BotMessage) => void;
}

/**
 * One editable canned message. The row is remounted (via its React `key`)
 * whenever the server row changes, so the draft always re-seeds from the
 * freshest version.
 */
function MessageRow({ message, spec, canManage, saving, onSave, onResetRequest }: MessageRowProps) {
  const [body, setBody] = useState(message.body);
  const [enabled, setEnabled] = useState(message.enabled);

  const dirty = body !== message.body || enabled !== message.enabled;
  const tooLong = body.length > MAX_BODY_LENGTH;
  const unknownPlaceholders = useMemo(
    () => findUnknownPlaceholders(body, message.placeholders),
    [body, message.placeholders],
  );
  const invalid = tooLong || unknownPlaceholders.length > 0 || body.trim().length === 0;

  let validationError: string | null = null;
  if (tooLong) {
    validationError = `Message is too long — ${body.length}/${MAX_BODY_LENGTH} characters.`;
  } else if (unknownPlaceholders.length > 0) {
    validationError = `Unknown placeholder${unknownPlaceholders.length > 1 ? 's' : ''}: ${unknownPlaceholders
      .map((name) => `{{${name}}}`)
      .join(', ')}`;
  }

  return (
    <Box sx={{ py: 2 }}>
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {spec.label}
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            color={message.customized ? 'primary' : 'default'}
            label={message.customized ? 'Customized' : 'Default'}
          />
          {message.customized && !message.enabled ? (
            <Chip size="small" variant="outlined" color="warning" label="Disabled — default used" />
          ) : null}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {spec.description}
        </Typography>

        <TextField
          size="small"
          fullWidth
          multiline
          minRows={2}
          value={body}
          disabled={!canManage || saving}
          onChange={(event) => setBody(event.target.value)}
          error={Boolean(validationError)}
          helperText={validationError ?? `${body.length}/${MAX_BODY_LENGTH}`}
          slotProps={{ htmlInput: { 'aria-label': spec.label } }}
        />

        {message.placeholders.length > 0 ? (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary">
              Placeholders:
            </Typography>
            {message.placeholders.map((name) => (
              <Chip
                key={name}
                size="small"
                label={`{{${name}}}`}
                onClick={canManage ? () => setBody((current) => `${current}{{${name}}}`) : undefined}
                sx={{ fontFamily: 'monospace' }}
              />
            ))}
          </Stack>
        ) : null}

        <Box
          sx={{
            bgcolor: 'action.hover',
            borderRadius: 2,
            px: 1.5,
            py: 1,
            maxWidth: 520,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Preview
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {substitutePreview(body)}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={enabled}
                disabled={!canManage || saving}
                onChange={(event) => setEnabled(event.target.checked)}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Active (off = the platform default is used)
              </Typography>
            }
          />
          <Box sx={{ flexGrow: 1 }} />
          {message.customized ? (
            <Button
              size="small"
              color="inherit"
              disabled={!canManage || saving}
              onClick={() => onResetRequest(message)}
            >
              Reset to default
            </Button>
          ) : null}
          <Button
            size="small"
            variant="contained"
            disabled={!canManage || !dirty || invalid || saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
            onClick={() => onSave(message, body, enabled)}
          >
            Save
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

interface FaqFormState {
  title: string;
  body: string;
  keywords: string;
  sortOrder: string;
  enabled: boolean;
}

function faqFormFromEntry(entry: FaqEntry | null): FaqFormState {
  return {
    title: entry?.title ?? '',
    body: entry?.body ?? '',
    keywords: entry?.keywords ?? '',
    sortOrder: entry?.sortOrder != null ? String(entry.sortOrder) : '',
    enabled: entry?.enabled ?? true,
  };
}

interface FaqDialogProps {
  open: boolean;
  entry: FaqEntry | null;
  saving: boolean;
  onClose: () => void;
  onSubmit: (form: FaqFormState) => void;
}

/** Create/edit dialog for one FAQ entry. Remounted per entry via `key`. */
function FaqDialog({ open, entry, saving, onClose, onSubmit }: FaqDialogProps) {
  const [form, setForm] = useState<FaqFormState>(() => faqFormFromEntry(entry));

  const tooLong = form.body.length > MAX_BODY_LENGTH;
  const invalid = form.title.trim().length === 0 || form.body.trim().length === 0 || tooLong;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{entry ? 'Edit FAQ entry' : 'Add FAQ entry'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Question"
            size="small"
            fullWidth
            value={form.title}
            disabled={saving}
            onChange={(event) => setForm((f) => ({ ...f, title: event.target.value }))}
          />
          <TextField
            label="Answer"
            size="small"
            fullWidth
            multiline
            minRows={3}
            value={form.body}
            disabled={saving}
            error={tooLong}
            helperText={
              tooLong
                ? `Answer is too long — ${form.body.length}/${MAX_BODY_LENGTH} characters.`
                : `${form.body.length}/${MAX_BODY_LENGTH}`
            }
            onChange={(event) => setForm((f) => ({ ...f, body: event.target.value }))}
          />
          <TextField
            label="Keywords"
            size="small"
            fullWidth
            value={form.keywords}
            disabled={saving}
            helperText="Comma-separated keywords — the bot matches free-text questions against them."
            onChange={(event) => setForm((f) => ({ ...f, keywords: event.target.value }))}
          />
          <TextField
            label="Sort order"
            size="small"
            type="number"
            value={form.sortOrder}
            disabled={saving}
            onChange={(event) => setForm((f) => ({ ...f, sortOrder: event.target.value }))}
            sx={{ maxWidth: 180 }}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={form.enabled}
                disabled={saving}
                onChange={(event) => setForm((f) => ({ ...f, enabled: event.target.checked }))}
              />
            }
            label="Active (used for keyword matching)"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={invalid || saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          onClick={() => onSubmit(form)}
        >
          {entry ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function TelegramBotMessagesPage() {
  const { canView, canManage } = useDomainPermissions('telegram');
  const messagesQuery = useBotMessages();
  const faqQuery = useFaqEntries();
  const upsertMessage = useUpsertBotMessage();
  const resetMessage = useResetBotMessage();
  const createFaq = useCreateFaqEntry();
  const updateFaq = useUpdateFaqEntry();
  const deleteFaq = useDeleteFaqEntry();
  const feedback = useFeedback();

  const [resetTarget, setResetTarget] = useState<BotMessage | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [faqDialog, setFaqDialog] = useState<{ open: boolean; entry: FaqEntry | null }>({
    open: false,
    entry: null,
  });
  const [faqDeleteTarget, setFaqDeleteTarget] = useState<FaqEntry | null>(null);

  const handleMutationError = (error: NormalizedApiError, contextMessage: string) => {
    if (error.status === 409) {
      feedback.error('This message changed elsewhere — the list has been refreshed. Review and save again.');
      void messagesQuery.refetch();
    } else if (error.status === 422) {
      feedback.error(error.message || 'The message failed validation.');
    } else {
      feedback.error(error.message || contextMessage);
    }
  };

  const handleSaveMessage = (message: BotMessage, body: string, enabled: boolean) => {
    setSavingKey(message.contentKey);
    upsertMessage.mutate(
      {
        contentKey: message.contentKey,
        body: {
          locale: message.locale || DEFAULT_BOT_MESSAGE_LOCALE,
          title: message.title,
          body,
          enabled,
          version: message.version,
        },
      },
      {
        onSuccess: () => feedback.propagation('Message saved.'),
        onError: (error) => handleMutationError(error, 'Could not save the message.'),
        onSettled: () => setSavingKey(null),
      },
    );
  };

  const handleResetConfirmed = () => {
    if (!resetTarget) {
      return;
    }
    const target = resetTarget;
    setResetTarget(null);
    setSavingKey(target.contentKey);
    resetMessage.mutate(target.contentKey, {
      onSuccess: () => feedback.propagation('Message reset to the platform default.'),
      onError: (error) => handleMutationError(error, 'Could not reset the message.'),
      onSettled: () => setSavingKey(null),
    });
  };

  const handleFaqSubmit = (form: FaqFormState) => {
    const payload = {
      locale: DEFAULT_BOT_MESSAGE_LOCALE,
      title: form.title.trim(),
      body: form.body,
      keywords: form.keywords.trim() === '' ? null : form.keywords.trim(),
      sortOrder: form.sortOrder.trim() === '' ? null : Number(form.sortOrder),
      enabled: form.enabled,
    };
    if (faqDialog.entry) {
      updateFaq.mutate(
        {
          contentKey: faqDialog.entry.contentKey,
          body: { ...payload, version: faqDialog.entry.version },
        },
        {
          onSuccess: () => {
            setFaqDialog({ open: false, entry: null });
            feedback.propagation('FAQ entry saved.');
          },
          onError: (error) => handleMutationError(error, 'Could not save the FAQ entry.'),
        },
      );
    } else {
      createFaq.mutate(payload, {
        onSuccess: () => {
          setFaqDialog({ open: false, entry: null });
          feedback.propagation('FAQ entry added.');
        },
        onError: (error) => handleMutationError(error, 'Could not add the FAQ entry.'),
      });
    }
  };

  const handleFaqDeleteConfirmed = () => {
    if (!faqDeleteTarget) {
      return;
    }
    const target = faqDeleteTarget;
    setFaqDeleteTarget(null);
    deleteFaq.mutate(target.contentKey, {
      onSuccess: () => feedback.propagation('FAQ entry deleted.'),
      onError: (error) => handleMutationError(error, 'Could not delete the FAQ entry.'),
    });
  };

  const faqSaving = createFaq.isPending || updateFaq.isPending;

  return (
    <SettingsScaffold
      title="Bot messages"
      subtitle="Customise your Telegram bot's canned replies and FAQ. Unset messages use the platform defaults; changes reach the bot within about a minute."
      icon={<ForumRoundedIcon />}
      query={messagesQuery}
      canView={canView}
      canManage={canManage}
      feedback={<FeedbackSnackbar feedback={feedback.feedback} onClose={feedback.clear} />}
    >
      {(data) => {
        const byKey = new Map(data.items.map((item) => [item.contentKey, item]));
        const extraMessages = data.items.filter((item) => !KNOWN_KEYS.has(item.contentKey));
        const faqEntries = [...(faqQuery.data?.items ?? [])].sort(
          (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.title.localeCompare(b.title),
        );

        return (
          <Stack spacing={2.5}>
            {MESSAGE_GROUPS.map((group) => {
              const known = group.keys
                .map((spec) => ({ spec, message: byKey.get(spec.key) }))
                .filter((entry): entry is { spec: MessageKeySpec; message: BotMessage } =>
                  Boolean(entry.message),
                );
              const extras =
                group.key === 'system'
                  ? extraMessages.map((message) => ({
                      spec: {
                        key: message.contentKey,
                        label: message.contentKey,
                        description: 'Additional platform message key.',
                      },
                      message,
                    }))
                  : [];
              const rows = [...known, ...extras];
              if (rows.length === 0) {
                return null;
              }
              return (
                <SettingsCard
                  key={group.key}
                  title={group.title}
                  description={group.description}
                  icon={group.icon}
                >
                  {rows.map(({ spec, message }) => (
                    <MessageRow
                      key={`${message.contentKey}|${message.version ?? 0}|${message.updatedAt ?? ''}`}
                      message={message}
                      spec={spec}
                      canManage={canManage}
                      saving={savingKey === message.contentKey}
                      onSave={handleSaveMessage}
                      onResetRequest={setResetTarget}
                    />
                  ))}
                </SettingsCard>
              );
            })}

            <SettingsCard
              title="FAQ"
              description="Merchant-authored answers the bot matches against free-text questions by keywords."
              icon={<LiveHelpRoundedIcon />}
              action={
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddRoundedIcon />}
                  disabled={!canManage}
                  onClick={() => setFaqDialog({ open: true, entry: null })}
                >
                  Add FAQ entry
                </Button>
              }
              divided={faqEntries.length > 0}
            >
              {faqQuery.isLoading ? (
                <LoadingState label="Loading FAQ…" />
              ) : faqQuery.isError ? (
                <Alert severity="error" sx={{ my: 1 }}>
                  Couldn't load the FAQ entries. {faqQuery.error?.message ?? ''}
                </Alert>
              ) : faqEntries.length === 0 ? (
                <EmptyState
                  title="No FAQ entries yet"
                  description="Add questions with keywords so the bot can answer free-text messages on its own."
                />
              ) : (
                faqEntries.map((entry) => (
                  <Box
                    key={entry.contentKey}
                    sx={{ py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}
                  >
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                        {entry.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                        {entry.keywords ? `Keywords: ${entry.keywords}` : 'No keywords'}
                      </Typography>
                    </Box>
                    {!entry.enabled ? (
                      <Chip size="small" variant="outlined" color="warning" label="Off" />
                    ) : null}
                    <Tooltip title="Edit">
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`Edit ${entry.title}`}
                          disabled={!canManage}
                          onClick={() => setFaqDialog({ open: true, entry })}
                        >
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`Delete ${entry.title}`}
                          disabled={!canManage}
                          onClick={() => setFaqDeleteTarget(entry)}
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                ))
              )}
            </SettingsCard>

            <Dialog open={resetTarget !== null} onClose={() => setResetTarget(null)}>
              <DialogTitle>Reset to default?</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  The custom text will be removed and the bot will use the platform default reply
                  for this message.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setResetTarget(null)}>Cancel</Button>
                <Button color="error" variant="contained" onClick={handleResetConfirmed}>
                  Reset
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog open={faqDeleteTarget !== null} onClose={() => setFaqDeleteTarget(null)}>
              <DialogTitle>Delete FAQ entry?</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  “{faqDeleteTarget?.title}” will be removed and the bot will stop using it for
                  keyword matching.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setFaqDeleteTarget(null)}>Cancel</Button>
                <Button color="error" variant="contained" onClick={handleFaqDeleteConfirmed}>
                  Delete
                </Button>
              </DialogActions>
            </Dialog>

            {faqDialog.open ? (
              <FaqDialog
                key={faqDialog.entry?.contentKey ?? 'new'}
                open={faqDialog.open}
                entry={faqDialog.entry}
                saving={faqSaving}
                onClose={() => setFaqDialog({ open: false, entry: null })}
                onSubmit={handleFaqSubmit}
              />
            ) : null}
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
